import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from '../context/TranslationContext';
import { useLoading } from '../context/LoadingContext';
import { getAiWorkingLanguage } from '../utils/routing-config-manager';
import { doActionCard, BUTTON_ACTION } from '../wa-tools/actionCardHandler';
import { CLASS_ICONS, BUTTON_ICON_MAP } from '../constants/icons';
import { config } from '../config/environment';
import Button from './Button';
import '../styles/components/ActionCard.css';

/**
 * Composant ActionCard pour afficher les actions disponibles
 * @param {Object} action - Structure de l'action
 * @param {Object} waItems - WaItems disponibles (mainWaItem, associatedWaItem et additionalWaItems)
 * @param {string} variant - Variante de style pour les boutons et l'apparence
 */
export const ActionCard = ({ action, waItems, variant = 'primary' }) => {
  const { t } = useTranslation();
  const { startLoading, stopLoading, showSuccess, updateLoadingMessage } = useLoading();
  const [aiWorkingLanguage, setAiWorkingLanguage] = useState('FR');

  // Charger la langue de travail au montage
  useEffect(() => {
    const loadLanguage = async () => {
      const lang = await getAiWorkingLanguage();
      if (lang) setAiWorkingLanguage(lang);
    };
    loadLanguage();
  }, []);

  if (!action) {
    return null;
  }

  // Récupérer les traductions
  const title = t('TOOLS', `${action.task_name}_TITLE`, action.task_name);
  const infoText = t('TOOLS', `${action.task_name}_TEXT`, `Action: ${action.task_name}`);

  // Déterminer l'icône principale selon task_icon
  const mainIconClass = CLASS_ICONS[action.function_tag?.slice(2)] || CLASS_ICONS.Default;

  // Déterminer les boutons à afficher
  const buttons = [];

  // Premier bouton : toujours présent avec l'icône de l'action
  const processButton = {
    action: (action.task_icon !== 'COPY' ? BUTTON_ACTION.PROCESS :  BUTTON_ACTION.COPY),
    iconClass: BUTTON_ICON_MAP[action.task_icon] || BUTTON_ICON_MAP.COMPUTE,
    key: BUTTON_ACTION.PROCESS,
    tooltip: t('TOOLS', `BUTTON_${action.task_icon || 'COMPUTE'}`) + 
             (config.DEBUG_MODE === 'true' ? '\n' + JSON.stringify(action, null, 2) : '')
  };
  
  buttons.push(processButton);

  // Deuxième bouton : selon les conditions
  if (action.task_icon !== 'COPY' && action.article_field_identification !== null) {
    // Bouton COPY
    buttons.push({
      action: BUTTON_ACTION.COPY,
      iconClass: BUTTON_ICON_MAP.COPY,
      key: BUTTON_ACTION.COPY,
      tooltip: t('TOOLS', 'BUTTON_COPY', `Action: ${BUTTON_ACTION.COPY}`)
    });
  } else if (action.tag_set_action !== null) {
    // Bouton FLAG
    buttons.push({
      action: BUTTON_ACTION.FLAG,
      iconClass: BUTTON_ICON_MAP.FLAG,
      key: BUTTON_ACTION.FLAG,
      tooltip: t('TOOLS', 'BUTTON_FLAG', `Action: ${BUTTON_ACTION.FLAG}`)
    });
  }

  // Gestionnaire de clic pour les boutons
  const handleButtonClick = (buttonAction) => {
    const loadingContext = { startLoading, stopLoading, showSuccess, updateLoadingMessage };
    doActionCard(buttonAction, action, waItems, loadingContext, aiWorkingLanguage);
  };

  return (
    <div className={`wac-container wac-container--${variant}`}>
      {/* Carré d'icône */}
      <div className={`wac-icon-container wac-icon-container--${variant}`}>
        <FontAwesomeIcon icon={mainIconClass} className="wac-icon" />
      </div>
      
      {/* Contenu principal */}
      <div className="wac-content">
        {/* Titre */}
        <div className="wac-title">
          {title}
        </div>
        
        {/* Ligne d'informations */}
        <div className="wac-info-line">
          {infoText}
        </div>
      </div>

      {/* Boutons d'action */}
      <div className="wac-buttons-container">
        {buttons.map((button, index) => (
          <Button
            key={button.key}
            variant={variant}
            size="sm"
            icon
            onClick={() => handleButtonClick(button.action)}
            title={button.tooltip || `Action: ${button.action}`}
            className="wac-action-button-unified"
          >
            <FontAwesomeIcon icon={button.iconClass} className="wac-button-icon" />
          </Button>
        ))}
      </div>
    </div>
  );
};

ActionCard.propTypes = {
  /** Structure de l'action */
  action: PropTypes.object.isRequired,
  /** WaItems disponibles (mainWaItem, associatedWaItem et additionalWaItems) */
  waItems: PropTypes.object.isRequired,
  /** Variante de style pour les boutons et l'apparence */
  variant: PropTypes.oneOf(['primary', 'secondary', 'success', 'warning', 'danger', 'ghost'])
};

export default ActionCard;
